import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ZapIcon } from 'lucide-react';
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}
function getNextThursdayMiami(): Date {
  // Get current time in Miami (America/New_York = Eastern Time)
  const now = new Date();
  // Create a formatter to get Miami's current date/time parts
  const miamiFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const parts = miamiFormatter.formatToParts(now);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '0';
  const miamiYear = parseInt(get('year'));
  const miamiMonth = parseInt(get('month')) - 1;
  const miamiDay = parseInt(get('day'));
  const miamiHour = parseInt(get('hour'));
  const miamiMinute = parseInt(get('minute'));
  const miamiSecond = parseInt(get('second'));
  // Build a Date object representing "now" in Miami local terms
  const miamiNow = new Date(miamiYear, miamiMonth, miamiDay, miamiHour, miamiMinute, miamiSecond);
  // Find next Thursday (day 4) at 19:00 (7 PM) Miami time
  const dayOfWeek = miamiNow.getDay(); // 0=Sun, 4=Thu
  let daysUntilThursday = (4 - dayOfWeek + 7) % 7;
  // If today IS Thursday, check if 7pm has already passed
  if (daysUntilThursday === 0) {
    const isPast7pm = miamiHour > 19 || miamiHour === 19 && (miamiMinute > 0 || miamiSecond > 0);
    if (isPast7pm) {
      daysUntilThursday = 7; // next week's Thursday
    }
  }
  // Build target date in Miami time
  const targetMiami = new Date(miamiYear, miamiMonth, miamiDay + daysUntilThursday, 19, 0, 0, 0);
  // Convert Miami local target back to UTC by finding the offset
  // Difference between real "now" and our miamiNow tells us the offset
  const offsetMs = now.getTime() - miamiNow.getTime();
  const targetUTC = new Date(targetMiami.getTime() + offsetMs);
  return targetUTC;
}
function getTargetDate(): Date {
  return getNextThursdayMiami();
}
function calculateTimeLeft(targetDate: Date): TimeLeft {
  const diff = targetDate.getTime() - new Date().getTime();
  if (diff <= 0) return {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor(diff / (1000 * 60 * 60) % 24),
    minutes: Math.floor(diff / (1000 * 60) % 60),
    seconds: Math.floor(diff / 1000 % 60)
  };
}
function pad(n: number): string {
  return String(n).padStart(2, '0');
}
export function TopBar() {
  const [targetDate] = useState<Date>(getTargetDate);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft(getTargetDate()));
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(targetDate));
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);
  return <motion.div initial={{
    y: -60,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} style={{
    background: 'linear-gradient(90deg, #0a0a0f 0%, #12001f 50%, #0a0a0f 100%)',
    borderBottom: '1px solid rgba(0,242,234,0.15)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%'
  }} data-id="element-107">
      <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '10px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '6px'
    }} data-id="element-108">
        {/* Left: LIVE indicator */}
        <div className="topbar-left" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexShrink: 0
      }} data-id="element-109">
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }} data-id="element-110">
            <div className="pulse-red" style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: '#f22a72',
            boxShadow: '0 0 8px rgba(242,42,114,0.8)'
          }} data-id="element-111" />
            <span style={{
            background: '#f22a72',
            color: 'white',
            fontSize: '11px',
            fontWeight: 900,
            fontFamily: 'Poppins, sans-serif',
            padding: '2px 8px',
            borderRadius: '4px',
            letterSpacing: '0.1em'
          }} data-id="element-112">
              EN VIVO
            </span>
          </div>
          <span className="topbar-label" style={{
          color: '#a0a0b0',
          fontSize: '13px',
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-113">
            Próxima sesión del Masterclass de Tiktok Shop Afiliados
          </span>
        </div>

        {/* Center: Countdown */}
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }} data-id="element-114">
          {[{
          value: timeLeft.days,
          label: 'días'
        }, {
          value: timeLeft.hours,
          label: 'hrs'
        }, {
          value: timeLeft.minutes,
          label: 'min'
        }, {
          value: timeLeft.seconds,
          label: 'seg'
        }].map((item, i) => <div key={item.label} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }} data-id="element-115">
              <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'rgba(24,163,159,0.08)',
            border: '1px solid rgba(24,163,159,0.25)',
            borderRadius: '6px',
            padding: '4px 6px',
            minWidth: '36px'
          }} data-id="element-116">
                <span className="countdown-digit" data-id="element-117">{pad(item.value)}</span>
                <span style={{
              color: '#6b6b80',
              fontSize: '9px',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.05em'
            }} data-id="element-118">
                  {item.label}
                </span>
              </div>
              {i < 3 && <span style={{
            color: 'var(--color-teal)',
            fontWeight: 900,
            fontSize: '18px',
            marginBottom: '8px'
          }} data-id="element-119">
                  :
                </span>}
            </div>)}
        </div>

        {/* Right: Scarcity */}
        <div className="topbar-scarcity" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0
      }} data-id="element-120">
          <ZapIcon size={14} color="var(--color-yellow)" data-id="element-121" />
          <span style={{
          color: 'var(--color-yellow)',
          fontSize: '13px',
          fontWeight: 700,
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-122">
            Solo quedan <strong data-id="element-123">47 cupos</strong> disponibles
          </span>
        </div>
      </div>

      <style data-id="element-124">{`
        @media (max-width: 640px) {
          .topbar-label { display: none !important; }
          .topbar-scarcity { display: none !important; }
          .topbar-left { justify-content: center; }
          .countdown-digit { font-size: 1.1rem !important; }
        }
      `}</style>
    </motion.div>;
}